import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Field, Heading, Label, Paragraph, Select } from '@digdir/designsystemet-react';
import { useLookUpBusinessMutation } from '../../../hooks/queries/organization/useBusinessProfileQuery';
import { SettingsSection } from '../../../components/TeamSettingsLayout';
import { BusinessTextField, bind, SectionProps } from './BusinessTextField';
import {
  dutiesFor,
  dutyText,
  employeesIn,
  isNotRegistered,
  ORGANIZATION_FORMS,
  organizationNumberError,
  withRegisteredFacts,
} from './businessView';
import classes from './OrganizationBusinessPage.module.css';

/** The registered facts about the business, which the admin can fetch from the Brønnøysund Register Centre */
export const CompanySection = (props: SectionProps & { fromSignup?: string | null }): ReactElement => {
  const { form, onChange } = props;
  const { t } = useTranslation();
  const lookUp = useLookUpBusinessMutation();
  const [invalidNumber, setInvalidNumber] = useState<string | null>(null);
  const duties = dutiesFor(form);
  const employees = employeesIn(form);
  const orgNumber = bind('orgNumber', props);
  const lookupError = lookUp.isError
    ? t(isNotRegistered(lookUp.error) ? 'organizationBusiness.lookup.notFound' : 'organizationBusiness.lookup.failed')
    : undefined;

  const fetchFacts = () => {
    const error = organizationNumberError(form.orgNumber);
    setInvalidNumber(error && t(`organizationBusiness.errors.${error}`));
    if (error) return;
    lookUp.mutate(form.orgNumber, { onSuccess: (facts) => onChange(withRegisteredFacts(form, facts)) });
  };

  return (
    <SettingsSection headingId="business-company" title={t('organizationBusiness.company.title')}>
      <div className={classes.lookup}>
        <BusinessTextField
          {...orgNumber}
          onChange={(value) => {
            setInvalidNumber(null);
            lookUp.reset();
            orgNumber.onChange(value);
          }}
          error={orgNumber.error ?? invalidNumber ?? lookupError}
          label={t('organizationBusiness.company.orgNumber')}
          description={t('organizationBusiness.company.orgNumberHint')}
          inputMode="numeric"
          className={classes.orgNumber}
        />
        <Button type="button" variant="secondary" loading={lookUp.isPending} onClick={fetchFacts}>
          {t(lookUp.isSuccess ? 'organizationBusiness.lookup.done' : 'organizationBusiness.lookup.action')}
        </Button>
      </div>
      {props.fromSignup && !lookUp.isSuccess && (
        <Alert data-color="info">
          <Paragraph data-size="sm">{props.fromSignup}</Paragraph>
        </Alert>
      )}
      {lookUp.isSuccess && (
        <Alert data-color="info" role="status">
          <Paragraph data-size="sm">{t('organizationBusiness.lookup.fetched')}</Paragraph>
        </Alert>
      )}
      <div className={classes.pair}>
        <BusinessTextField
          {...bind('legalName', props)}
          label={t('organizationBusiness.company.legalName')}
          autoComplete="organization"
        />
        <Field>
          <Label htmlFor="business-organizationForm">{t('organizationBusiness.company.organizationForm')}</Label>
          <Select
            id="business-organizationForm"
            value={form.organizationForm}
            onChange={(event) =>
              onChange({ organizationForm: ORGANIZATION_FORMS.find((value) => value === event.target.value) ?? '' })
            }
          >
            {!form.organizationForm && (
              <Select.Option value="">{t('organizationBusiness.company.chooseForm')}</Select.Option>
            )}
            {ORGANIZATION_FORMS.map((value) => (
              <Select.Option key={value} value={value}>
                {t(`organizationBusiness.forms.${value}`)}
              </Select.Option>
            ))}
          </Select>
        </Field>
      </div>
      <BusinessTextField
        {...bind('industryCode', props)}
        label={t('organizationBusiness.company.industryCode')}
        description={t('organizationBusiness.company.industryCodeHint')}
      />
      <div className={classes.address}>
        <BusinessTextField
          {...bind('businessAddress', props)}
          label={t('organizationBusiness.company.businessAddress')}
          autoComplete="street-address"
        />
        <BusinessTextField
          {...bind('postalCode', props)}
          label={t('organizationBusiness.company.postalCode')}
          autoComplete="postal-code"
          inputMode="numeric"
        />
        <BusinessTextField
          {...bind('postalPlace', props)}
          label={t('organizationBusiness.company.postalPlace')}
          autoComplete="address-level2"
        />
      </div>
      <BusinessTextField
        {...bind('employeeCount', props)}
        type="number"
        inputMode="numeric"
        label={t('organizationBusiness.company.employeeCount')}
        description={t('organizationBusiness.company.employeeCountHint')}
        className={classes.employees}
      />
      {duties.length > 0 && (
        <Alert data-color="info" className={classes.duties}>
          <Heading level={3} data-size="2xs">
            {t('organizationBusiness.duties.title', { count: employees })}
          </Heading>
          <ul className="ds-list">
            {duties.map((duty) => (
              <li key={duty} className="ds-paragraph" data-size="sm">
                {dutyText(duty, employees, t)}
              </li>
            ))}
          </ul>
        </Alert>
      )}
    </SettingsSection>
  );
};
